<template>
  <div>
    <LayoutContent>
      <SectionTitle :label="$t('label.transferToBank')" />
      <v-card class="rounded-lg overflow-hidden elevation-3">
        <section class="mt-10 px-8">
          <SectionTitle :label="$t('label.addBankAccount')" class="ma-0" />
        </section>
        <v-divider class="mb-10 grey lighten-2" />
        <section class="px-8 mb-10" style="min-height: 300px">
          <div v-if="currentStep === 0 && bankList.length > 0">
            <p class="font-weight-medium text-h6 mb-6">{{ $t('label.selectBank') }}</p>
            <v-row>
              <v-col :sm="6" :lg="2" :cols="12" v-for="bank in bankList" :key="bank.bank_id">
                <div class="d-flex flex-column text-center">
                  <div
                    class="rounded elevation-2"
                    :style="{
                      border: form.bank_id === bank.id ? '1px solid #109A50' : null
                    }"
                    @click="selectBank(bank)"
                  >
                    <div class="px-8 py-4 white rounded">
                      <v-img :src="bank.image" :aspect-ratio="2.8" class="white" />
                    </div>
                  </div>
                  <div class="text-subtitle-2 mt-3">
                    {{ bank.bank_name }}
                  </div>
                </div>
              </v-col>
            </v-row>
          </div>
          <div v-else-if="currentStep === 1">
            <v-form ref="transferForm" v-if="currentAgent">
              <section class="bank-account-details pa-8 mx-auto" :class="$vuetify.breakpoint.smAndUp ? 'px-16' : 'px-2'">
                <SectionTitle :label="$t('label.bankAccountDetails')" />
                <div class="bank-fields--wrapper">
                  <fieldset>
                    <label for="form.bank_id" class="font-weight-bold">{{ $t('label.bankName') }}</label>
                    <v-select
                      v-model="form.bank_id"
                      :items="bankList"
                      item-value="id"
                      item-text="bank_name"
                      :placeholder="$t('placeholder.bankName')"
                      filled
                      id="bankName"
                      class="no-input-underline rounded center-appended-icon"
                      :rules="$rules.requiredRules"
                    >
                      <template v-slot:append>
                        <v-btn fab depressed x-small color="transparent" class="disable-hover-bg" :ripple="false"><v-icon class="transparent">mdi-chevron-down</v-icon></v-btn>
                      </template>
                      <template slot="item" slot-scope="data">
                        <img width="50px" height="20px" :src="data.item.image" class="mr-4" />
                        {{ data.item.bank_name }}
                      </template>
                    </v-select>
                  </fieldset>
                  <fieldset>
                    <label for="form.account_type" class="font-weight-bold">{{ $t('label.bankType') }}</label>
                    <v-select
                      v-model="form.account_type"
                      :items="accountType"
                      item-value="id"
                      item-text="label"
                      :placeholder="$t('placeholder.bankType')"
                      filled
                      id="bankType"
                      class="no-input-underline rounded center-appended-icon"
                      :rules="$rules.requiredRules"
                    >
                      <template v-slot:append>
                        <v-btn fab depressed x-small color="transparent" class="disable-hover-bg" :ripple="false"><v-icon class="transparent">mdi-chevron-down</v-icon></v-btn>
                      </template>
                    </v-select>
                  </fieldset>
                  <fieldset>
                    <label for="form.account_no" class="font-weight-bold">{{ $t('label.bankAccountNo') }}</label>
                    <v-text-field v-model="form.account_no" filled id="bankAccountNumber" class="no-input-underline rounded center-appended-icon" :rules="$rules.requiredRules" />
                  </fieldset>
                  <fieldset>
                    <label for="bankAccountName" class="font-weight-bold">{{ $t('label.bankAccountHolderName') }}</label>
                    <v-text-field v-model="form.account_holder_name" filled id="bankAccountName" class="no-input-underline rounded center-appended-icon" :rules="$rules.requiredRules" />
                  </fieldset>
                  <fieldset>
                    <label for="form.email" class="font-weight-bold">{{ $t('label.emailAddress') }}</label>
                    <v-text-field v-model="form.email" filled id="emailAddress" class="no-input-underline rounded center-appended-icon" :rules="$rules.emailRules" />
                  </fieldset>
                </div>
              </section>
            </v-form>
          </div>
        </section>
      </v-card>
    </LayoutContent>
    <bottom-bar>
      <v-container class="py-3 d-flex align-center justify-center">
        <app-button v-if="currentStep === 0" :label="$t('button.next')" class="mr-2" width="12rem" :disabled="form.bank_id ? false : true" @click="currentStep = 1" />
        <app-button v-if="currentStep === 1" :label="$t('button.save')" class="mr-2" width="12rem" @click="saveForm" />
      </v-container>
    </bottom-bar>
  </div>
</template>

<script>
import SectionTitle from '@/components/SectionTitle.vue'
import LayoutContent from '@/views/layouts/LayoutContent.vue'
import { mapState, mapActions, mapGetters } from 'vuex'
import { localeHelper } from '@/utils'

export default {
  name: 'AddedBankAccount',
  components: { LayoutContent, SectionTitle },
  data: () => ({
    bankList: [],
    selectedBank: '',
    currentStep: 0,
    accountType: [
      { id: 1, label: localeHelper.getMessage('label.individual') },
      { id: 2, label: localeHelper.getMessage('label.company') }
    ],
    form: {
      bank_id: null,
      account_type: null,
      account_no: null,
      account_holder_name: '',
      email: ''
    }
  }),
  computed: {
    ...mapGetters('page', ['agent_min_withdrawal_amount']),
    ...mapState('agent', ['currentAgent'])
  },
  created() {
    this.getBankList().then(({ bank_list }) => {
      this.bankList = bank_list
    })
  },
  methods: {
    ...mapActions('agent', ['getBankList', 'addUserWithdrawalBank']),
    selectBank(bank) {
      if (this.form.bank_id === bank.id) this.form.bank_id = ''
      else this.form.bank_id = bank.id
    },
    saveForm() {
      if (!this.$refs.transferForm.validate()) {
        return true
      }
      this.addUserWithdrawalBank(this.form)
        .then((res) => {
          this.$router.push({ name: this.$constants.ROUTE_NAME.AGENT_TRANSFER_TO_BANK })
        })
        .catch((err) => this.$toast.error(err.error_message))
    }
  }
}
</script>

<style lang="scss" scoped>
.coin-conversion--container {
  display: grid;
  grid-template-columns: 2fr 3fr 3fr;
}

.coin-options--wrapper {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
}

.coin-option {
  border-color: var(--v-primary-lighten1) !important;
}

.bank-account-details {
  max-width: 1000px;
}

.bank-fields--wrapper {
  display: grid;
  grid-template-columns: 1fr;
  column-gap: max(2rem, 7.5vw);
  @media only screen and (min-width: 1000px) {
    grid-template-columns: 1fr 1fr;
  }
}
</style>
